<template>
  <paginated-table
    id="admin-invitations-table"
    ref="table"
    endpoint="invitations"
    :columns="columns"
    :label="$t('views.admin.invitations.title')"
    :fetch-params="params"
    :initial-filters="{
      status: 'active',
    }"
    sync-filters-with-url
    :show-generate-csv="false"
  >
    <template #head-buttons>
      <icon-button role="add" :to="createInvitationLink">
        {{ $t("views.admin.invitations.actions.invite") }}
      </icon-button>
    </template>
    <template #cell(actions)="{ item }">
      <b-dropdown
        v-if="item.status === 'active'"
        size="sm"
        variant="white-primary"
        no-caret
        toggle-class="mr-0"
      >
        <template #button-content>
          <b-icon icon="three-dots-vertical" /><span class="sr-only">actions</span>
        </template>
        <b-dropdown-item
          variant="danger"
          size="sm"
          :disabled="cancelingIds.includes(item.id)"
          @click="() => cancelInvitation(item)"
        >
          <b-spinner v-if="cancelingIds.includes(item.id)" small />
          <b-icon v-else icon="trash" />
          {{ $t("forms.cancel") | capitalize }}
        </b-dropdown-item>
      </b-dropdown>

      <div v-else>
        <!-- default to nothing -->
      </div>
    </template>
  </paginated-table>
</template>

<script>
import IconButton from "@/components/shared/IconButton.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column } from "@/components/shared/PaginatedTableColumns";
import { extractErrors } from "@/helpers";
import { capitalize, date } from "@/helpers/filters";
import { communityLink } from "@/helpers/links";
import { getAdminCommunities } from "@/helpers/permissions/communities";
import { isGlobalAdmin } from "@/helpers/permissions/users";
import { post } from "@/requests/server";
import dayjs from "dayjs";

export default {
  name: "AdminInvitations",
  components: {
    IconButton,
    PaginatedTable,
  },
  data() {
    const columns = [
      new Column("id", capitalize(this.$t("lists.id")), "id", { showByDefault: false }),
      new Column("email", capitalize(this.$t("users.fields.email"))),
      Column.withRelation(
        "inviter.full_name",
        this.$t("views.admin.invitations.columns.invited_by"),
        {
          relation: "users",
          label: "full_name",
          field: "id",
          params: { for: "admin" },
        },
        "inviter_user_id",
        {
          urlFct: (item) => `/admin/users/${item.inviter.id}`,
        }
      ),
      new Column("reason", this.$t("views.admin.invitations.columns.reason")),
      Column.withSelect("auto_approve", this.$t("views.admin.invitations.columns.auto_approve"), [
        { value: null, label: this.$t("views.admin.invitations.auto_approve.all") },
        {
          value: true,
          label: this.$t("views.admin.invitations.auto_approve.yes"),
          variant: "success",
        },
        {
          value: false,
          label: this.$t("views.admin.invitations.auto_approve.no"),
          variant: "primary",
        },
      ]),
      Column.withSelect(
        "status",
        this.$t("views.admin.invitations.columns.status"),
        [
          { value: null, label: this.$t("views.admin.invitations.status.all") },
          {
            value: "active",
            label: this.$t("views.admin.invitations.status.active"),
            variant: "primary",
          },
          {
            value: "accepted",
            label: this.$t("views.admin.invitations.status.accepted"),
            variant: "success",
          },
          {
            value: "refused",
            label: this.$t("views.admin.invitations.status.refused"),
            variant: "danger",
          },
          {
            value: "canceled",
            label: this.$t("views.admin.invitations.status.canceled"),
            variant: "danger",
          },
          {
            value: "expired",
            label: this.$t("views.admin.invitations.status.expired"),
            variant: "warning",
          },
        ],
        { sortable: false }
      ),
      new Column("expires_at", this.$t("views.admin.invitations.columns.expires_at"), "date", {
        showByDefault: false,
        formatter: date,
      }),
      new Column("created_at", this.$t("views.admin.invitations.columns.created_at"), "date", {
        showByDefault: false,
      }),
      new Column("accepted_at", this.$t("views.admin.invitations.columns.accepted_at"), "date", {
        showByDefault: false,
      }),
      new Column("refused_at", this.$t("views.admin.invitations.columns.refused_at"), "date", {
        showByDefault: false,
      }),
      new Column("canceled_at", this.$t("views.admin.invitations.columns.canceled_at"), "date", {
        showByDefault: false,
      }),
    ];

    if (isGlobalAdmin(this.$store.state.user)) {
      columns.splice(
        3,
        0,
        Column.withRelation(
          "community.name",
          this.$t("communities.fields.community.name"),
          {
            relation: "communities",
            label: "name",
            field: "id",
            params: { for: "admin" },
          },
          "community_id",
          {
            urlFct: (item) => communityLink(item.community),
          }
        )
      );
    }

    return {
      cancelingIds: [],
      columns,
    };
  },
  computed: {
    canCancelInvitation() {
      return true;
    },
    communityId() {
      // Returns the community of the admin if they're admin of a single community
      if (isGlobalAdmin(this.$store.state.user)) {
        return null;
      }
      const adminCommunities = getAdminCommunities(this.$store.state.user);
      if (adminCommunities.length === 1) {
        return adminCommunities[0].id;
      }
      return null;
    },
    params() {
      if (this.communityId) {
        return { community_id: this.communityId };
      }
      return {};
    },
    createInvitationLink() {
      if (this.communityId) {
        return `/admin/invitations/new?community_id=${this.communityId}`;
      }
      return "/admin/invitations/new";
    },
  },
  methods: {
    dayjs,
    async cancelInvitation(item) {
      try {
        this.cancelingIds.push(item.id);
        await post(`invitations/${item.id}/cancel`, null, {
          notifications: { action: this.$t("views.admin.invitations.notifications.cancel.action") },
        });

        this.$refs.table.refresh();
      } catch (e) {
        const content = extractErrors(e.response?.data).join(", ") ?? "";

        this.$store.commit("addNotification", {
          content,
          title: this.$t("views.admin.invitations.notifications.cancel.error"),
          variant: "danger",
        });
      } finally {
        this.cancelingIds = this.cancelingIds.filter((id) => id !== item.id);
      }
    },
  },
};
</script>

<style lang="scss"></style>
